/**
 * App-wide polyfills. Imported as the very first statement in `src/index.tsx`
 * so it runs before any dependency's module code evaluates.
 *
 * ## crypto.randomUUID in non-secure contexts
 *
 * `crypto.randomUUID()` is only exposed in
 * [secure contexts](https://developer.mozilla.org/en-US/docs/Web/Security/Secure_Contexts):
 * `https://*`, the literal hostnames `localhost` / `127.0.0.1` / `[::1]`,
 * `file://`, and extensions. Browsers decide this on the hostname string, not
 * on DNS resolution — so `http://local.devtempus.com` (mapped to 127.0.0.1 via
 * /etc/hosts for local dev) is NOT a secure context and `crypto.randomUUID` is
 * undefined there.
 *
 * `giorules-jdm-editor` (and other third-party libraries) call
 * `crypto.randomUUID()` directly from their compiled dist (13+ call sites in
 * giorules-jdm-editor alone — connect-edge handlers, node id minting, zod
 * schema defaults, etc.), which we cannot patch via our util + lint rule.
 * The only way to protect those callers is to install `crypto.randomUUID` as
 * a global polyfill BEFORE any library loads.
 *
 * The fallback synthesizes a RFC 4122 v4 UUID from `crypto.getRandomValues`,
 * which has no secure-context restriction and draws from the same CSPRNG.
 * The implementation is deliberately duplicated with `utils/misc.ts :: randomUUID`
 * so this file has zero imports beyond the global `crypto` and can run before
 * anything else — importing `utils/misc` would pull React / axios / moment /
 * yaml into the polyfill boot path.
 */
if (typeof crypto.randomUUID !== 'function') {
  const fallback = (): `${string}-${string}-${string}-${string}-${string}` => {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` as `${string}-${string}-${string}-${string}-${string}`;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (crypto as any).randomUUID = fallback;
}

export {};
